import { useState } from "react";
import { UserIcon } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Column, EmptyState, ErrorText } from "@/components";
import { AutosaveFormRichTextInput } from "@/components/companies/shared/autosave-form/AutosaveFormRichTextInput";
import { AutosaveFormTextInput } from "@/components/companies/shared/autosave-form/AutosaveFormTextInput";
import { IntakeForm } from "@/components/companies/shared/IntakeForm";
import { IntakeGuidance } from "@/components/companies/shared/IntakeGuidance";
import { IntakeTab } from "@/components/companies/shared/IntakeTab";
import { IntakeTabBar } from "@/components/companies/shared/IntakeTabBar";
import { ReadOnlyRichTextField } from "@/components/companies/shared/ReadOnlyField";

import { useUpdateSetJobIntake } from "@/hooks/updateSets/useUpdateSetJobIntake";
import { EMPTY_PROGRESS } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { IdealCandidateProfile, JobIntake, Progress } from "@/types";

const profileSchema = z.object({
  id: z.string(),
  personaTitle: z.string().nullable(),
  backgroundDetails: z.string().nullable(),
  experienceDetails: z.string().nullable(),
  personality: z.string().nullable(),
});

const emptyProfile = (): IdealCandidateProfile => ({
  id: crypto.randomUUID(),
  personaTitle: null,
  backgroundDetails: null,
  experienceDetails: null,
  personality: null,
});

export const IdealCandidateProfiles = ({
  isEditing,
  jobIntake,
  setIsEditing,
  itemsProgress,
}: {
  isEditing: boolean;
  jobIntake: JobIntake;
  setIsEditing: (isEditing: boolean) => void;
  itemsProgress: Record<string, Progress>;
}) => {
  /* Database CRUD Operations */
  // Update
  const { mutate: updateJobIntake, isError } = useUpdateSetJobIntake();

  /* React Hooks */
  // useState
  const [profiles, setProfiles] = useState<IdealCandidateProfile[]>(
    jobIntake.idealCandidateProfiles,
  );
  const [selectedIndex, setSelectedIndex] = useState(0);

  /* Handlers */
  const saveAll = (updated: IdealCandidateProfile[]) => {
    updateJobIntake({
      updateJobIntakeRequest: { idealCandidateProfiles: updated },
    });
  };

  const handleAdd = () => {
    const updated = [...profiles, emptyProfile()];
    setProfiles(updated);
    setSelectedIndex(updated.length - 1);
    setIsEditing(true);
    saveAll(updated);
  };

  const handleDelete = (id: string) => {
    const deletedIndex = profiles.findIndex((p) => p.id === id);
    const updated = profiles.filter((p) => p.id !== id);
    setProfiles(updated);
    const newIndex =
      deletedIndex < selectedIndex ? selectedIndex - 1 : selectedIndex;
    setSelectedIndex(Math.min(Math.max(0, newIndex), updated.length - 1));
    saveAll(updated);
  };

  const handleUpdate = (data: IdealCandidateProfile) => {
    mixpanelUtils.trackFormSubmission("Ideal candidate profile", MIXPANEL_SCREENS.JOB);
    const updated = profiles.map((p) => (p.id === data.id ? data : p));
    setProfiles(updated);
    saveAll(updated);
  };

  if (!profiles.length) {
    return (
      <Column className="gap-6">
        <EmptyState
          title="No ideal candidate profiles yet"
          description="Add a profile to define your ideal candidate."
          Icon={UserIcon}
          primaryButtonName="Add profile"
          handlePrimaryClick={() => {
            setIsEditing(true);
            handleAdd();
          }}
        />
      </Column>
    );
  }

  return (
    <Column className="gap-4">
      {/* top offset = combined height of IntakeHeader */}
      <Column className="sticky top-[8.78rem] z-10 gap-4 bg-white pb-2">
        <IntakeGuidance message="Consider your most successful employees in this role. What does their background look like, and what sets them apart from others?" />
        <IntakeTabBar
          addLabel="Add Profile"
          deleteDescription="This will permanently delete this ideal candidate profile."
          onAdd={handleAdd}
          onDelete={() => {
            const selected = profiles[selectedIndex];
            handleDelete(selected.id);
          }}
          screenName={MIXPANEL_SCREENS.JOB}
          trackingName="Ideal Candidate Profile"
        >
          {profiles.map((profile, index) => (
            <IntakeTab
              key={profile.id}
              progress={itemsProgress[profile.id] ?? EMPTY_PROGRESS}
              isSelected={selectedIndex === index}
              label={profile.personaTitle ?? `Profile ${String(index + 1)}`}
              onSelect={() => {
                setSelectedIndex(index);
              }}
            />
          ))}
        </IntakeTabBar>
      </Column>
      {isEditing ? (
        <ProfileForm
          key={profiles[selectedIndex]?.id ?? "empty"}
          onSave={handleUpdate}
          profile={profiles[selectedIndex] ?? emptyProfile()}
        />
      ) : (
        <ProfileReadOnly profile={profiles[selectedIndex] ?? emptyProfile()} />
      )}
      {isError && (
        <ErrorText errorMessage="An error occurred while saving. Please try again." />
      )}
    </Column>
  );
};

const ProfileForm = ({
  onSave,
  profile,
}: {
  onSave: (data: IdealCandidateProfile) => void;
  profile: IdealCandidateProfile;
}) => {
  const form = useForm<z.infer<typeof profileSchema>>({
    resolver: zodResolver(profileSchema),
    defaultValues: profile,
  });

  const saveOnBlur = () => {
    void form.handleSubmit((data) => {
      onSave(data);
    })();
  };

  return (
    <IntakeForm form={form} onSave={saveOnBlur}>
      <Column className="gap-6">
        <AutosaveFormTextInput
          fieldName="personaTitle"
          form={form}
          label="Persona Title"
          onBlur={saveOnBlur}
          placeholder="e.g., Experienced Machinist, Recent Graduate"
        />
        <AutosaveFormRichTextInput
          editorHeight="150px"
          fieldName="backgroundDetails"
          form={form}
          infoTooltip="Relevant industries, company types or specific companies, education, and certifications."
          label="Background Details"
          onBlur={saveOnBlur}
        />
        <AutosaveFormRichTextInput
          editorHeight="150px"
          fieldName="experienceDetails"
          form={form}
          infoTooltip="Years of experience, key skills mastered, and machinery or technology expertise with proficiency levels."
          label="Experience Details"
          onBlur={saveOnBlur}
        />
        <AutosaveFormRichTextInput
          editorHeight="150px"
          fieldName="personality"
          form={form}
          infoTooltip={`What are some general personality traits that describe this persona?\nWhat kind of attitude or work style tends to make someone successful in this role?`}
          label="Personality"
          onBlur={saveOnBlur}
        />
      </Column>
    </IntakeForm>
  );
};

const ProfileReadOnly = ({ profile }: { profile: IdealCandidateProfile }) => (
  <Column className="gap-4">
    <ReadOnlyRichTextField
      label="Persona Title"
      value={profile.personaTitle ?? null}
    />
    <ReadOnlyRichTextField
      label="Background Details"
      value={profile.backgroundDetails ?? null}
    />
    <ReadOnlyRichTextField
      label="Experience Details"
      value={profile.experienceDetails ?? null}
    />
    <ReadOnlyRichTextField
      label="Personality"
      value={profile.personality ?? null}
    />
  </Column>
);
